import { Component, ElementRef, OnInit } from '@angular/core';

interface FeaturedTextBlockTag {
    Text: string;
    Url: string;
}

interface FeaturedTextBlockViewModel {
    Tags?: FeaturedTextBlockTag[];
    Date?: string;
    Headline?: string;
    Summary?: string;
    LinkUrl?: string;
}

@Component({
    selector: 'featured-text-block',
    templateUrl: './featured-text-block.component.html',
    // Font sizes are in rem so they follow the reader's browser text size; padding stays in px so
    // larger text gets more room instead of the panel's padding growing with it.
    styles: [`
        .swapa-featured-text-block-text {
            background-color: rgba(217, 217, 217, 0.12);
            padding: 32px 24px;
        }

        .swapa-featured-text-block-meta {
            font-family: "Nimbus Sans", Arial, sans-serif;
            font-size: 1rem;
            line-height: 1.2;
            letter-spacing: 0.8px;
        }

        .swapa-featured-text-block-tag {
            color: #F37021;
        }

        .swapa-featured-text-block-separator,
        .swapa-featured-text-block-date {
            color: #979797;
        }

        .swapa-featured-text-block-title {
            color: #084F71;
            font-family: "Nimbus Sans", Arial, sans-serif;
            font-size: 2rem;
            line-height: 1.2;
            letter-spacing: 0.144px;
            overflow-wrap: anywhere;
        }

        .swapa-featured-text-block-description {
            color: #414042;
            font-family: "Nimbus Sans", Arial, sans-serif;
            font-size: 1.125rem;
            line-height: 1.55;
            letter-spacing: 0.1px;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 5;
            overflow: hidden;
        }

        /* Phones: tag and date on separate lines, 28px title, 16px description. */
        @media (max-width: 767.98px) {
            .swapa-featured-text-block-text {
                padding: 55px 38px;
            }

            .swapa-featured-text-block-meta {
                font-size: 0.9375rem;
            }

            .swapa-featured-text-block-separator {
                display: none;
            }

            .swapa-featured-text-block-date {
                display: block;
            }

            .swapa-featured-text-block-title {
                font-size: 1.75rem;
            }

            .swapa-featured-text-block-description {
                font-size: 1rem;
                line-height: 1.35;
                letter-spacing: 0.08px;
            }
        }

        /* Tablet: the panel is half of a 720px container. */
        @media (min-width: 768px) and (max-width: 991.98px) {
            .swapa-featured-text-block-text {
                padding: 40px 32px;
            }

            .swapa-featured-text-block-title {
                font-size: 2.125rem;
            }
        }

        /* Small desktop: half of a 960px container is too narrow for the full desktop padding and 48px title. */
        @media (min-width: 992px) and (max-width: 1199.98px) {
            .swapa-featured-text-block-text {
                padding: 56px 48px;
            }

            .swapa-featured-text-block-title {
                font-size: 2.5rem;
            }

            .swapa-featured-text-block-description {
                font-size: 1.25rem;
            }
        }

        /* Desktop */
        @media (min-width: 1200px) {
            .swapa-featured-text-block-text {
                padding: 80px 86.4px;
            }

            .swapa-featured-text-block-title {
                font-size: 3rem;
            }

            .swapa-featured-text-block-description {
                font-size: 1.25rem;
            }
        }
    `],
})
export class FeaturedTextBlockComponent implements OnInit {
    public config: FeaturedTextBlockViewModel | null = null;

    constructor(private host: ElementRef<HTMLElement>) { }

    ngOnInit(): void {
        // The server sends the whole card as JSON in an attribute; attributes survive Angular taking over the host element.
        const json = this.host.nativeElement.getAttribute('config');
        if (!json) {
            return;
        }

        try {
            this.config = JSON.parse(json) as FeaturedTextBlockViewModel;
        } catch {
            this.config = null;
        }
    }
}
